import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { FilterChipAutosizeHelper } from '@zaoblako/web/shared/helpers/filter-chip-autosize.helper';
import { FilterSelectChipUiHelper } from '@zaoblako/web/shared/helpers/filter-select-chip-ui.helper';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { isSameId } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import { FilterBySelectedItemsPipe } from '@zaoblako/web/shared/pipes/filter-by-selected-items.pipe';
import { NestedPropertyPipe } from '@zaoblako/web/shared/pipes/nested-property.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

import { isNil } from 'lodash';
import { FloatLabelModule } from 'primeng/floatlabel';
import {
  MultiSelect,
  MultiSelectFilterEvent,
  MultiSelectLazyLoadEvent,
  MultiSelectModule
} from 'primeng/multiselect';

@Component({
  standalone: true,
  selector: 'crm-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  },
  imports: [
    CommonModule,
    MultiSelectModule,
    OrderByPipe,
    FormsModule,
    NestedPropertyPipe,
    SafeHtmlPipe,
    FloatLabelModule,
    FilterBySelectedItemsPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent implements OnInit, OnChanges, AfterContentInit, AfterViewInit, OnDestroy {
  @ViewChild('multiSelect') public readonly multiSelect: MultiSelect;
  public isNotIdArray = false;

  @Input() public set options(array: any[]) {
    this.runWithoutSelectionEmit(() => this.buildOptions(array));
  }

  @Input() public set selectedItems(selectedItems: any[]) {
    const next = selectedItems ?? [];
    if (this.hasSameSelectedItemsInput(next)) {
      return;
    }

    this.lastSelectedItemsInput = next;
    this.runWithoutSelectionEmit(() => this.buildSelectedItems(this.lastSelectedItemsInput));
  }

  public _options: any[] = [];
  public _selectedItems: any[] = [];
  private lastSelectedItemsInput: any[] = [];
  private selectionEmitSuppressDepth = 0;

  @Output() public selectedItemsChange = new EventEmitter<any>();
  @Input() public placeholder = '\u00A0';
  @Input() public inputId = Math.random().toString();
  @Input() public label: string;
  @Input() public itemDescriptionField: string;
  @Input() public itemSubDescriptionField: string;
  @Input() public maxSelectedLabels = 2;
  @Input() public showHeader = true;
  @Input() public optionLabel = 'name';
  @Input() public optionValue = 'id';
  @Input() public sort = this.optionLabel;
  @Input() public disabled = false;
  @Input() public group = false;
  @Input() public filterBy = 'name';
  @Input() public emptyFilterMessage = 'Нет результатов';
  @Input() public emptyMessage = 'Список пуст';
  @Input() public optionDisabled = 'inactive';
  @Input() public selectedItemsLabel = 'Выбрано: {0}';
  @Input() public appendTo: any;
  @Input() public scrollHeight = '300px';
  @Input() public display = 'chip';
  @Input() public showToggleAll = false;
  @Input() public lazy = false;
  @Input() public moveDown = false;
  @Input() public showClear = true;
  @Input() public isFilterSelectedItems = false;
  @Input() public itemStyleClass: string;
  @Input() public isError = false;
  /** PrimeNG loading spinner on the trigger (options / selection hydrate). */
  @Input() public loading = false;
  /**
   * null = авто: при v2 и непустом `label` — плавающая подпись (как в фильтрах); иначе как раньше.
   * false — всегда подпись в placeholder; true — всегда float.
   */
  @Input() public isFloatLabel: boolean | null = null;

  @Output() public changeValue = new EventEmitter<any>();
  @Output() public panelHide = new EventEmitter<any>();
  @Output() public panelShow = new EventEmitter<any>();
  @Output() public openedChange = new EventEmitter<boolean>();
  @Output() public onLazyLoad = new EventEmitter<MultiSelectLazyLoadEvent>();
  @Output() public onFilter = new EventEmitter<MultiSelectFilterEvent>();

  @Input() public isSendFromChange = false;
  @Output() public onFinalChange = new EventEmitter<any>();
  private readonly TIMER_FULL_CHANGE = 700;
  private timerFromChange: any;

  @Input() public class = '';
  @Input() public styleClass = '';
  @Input() public panelStyle: any;
  @Input() public panelStyleClass: string;

  /** V2 — «пилюли» и палитра FRM 2.0 (см. `_filters-ui-v2.scss`). По умолчанию v2. */
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize = 40;

  @ContentChild('groupTemplate', { static: false }) public readonly groupTemplate: TemplateRef<any>;
  @ContentChild('itemTemplate', { static: false }) public readonly itemTemplate: TemplateRef<any>;
  @ContentChild('selectedItemsTemplate', { static: false }) public readonly selectedItemsTemplate: TemplateRef<any>;
  @ContentChild('footerTemplate', { static: false }) public readonly footerTemplate: TemplateRef<any>;

  @Input() public isTemplateCombined = false;

  public replaceRegExp = /{[0-9]+}/g;
  public isDisplayChip = false;

  public get mergedPanelStyleClass(): string {
    return FilterSelectChipUiHelper.mergePanelStyleClass(this.panelStyleClass, this.filtersUiVersion);
  }

  /** Методы (не геттеры) — надёжнее для strict-проверки шаблона Angular. */
  public usesFloatLabel(): boolean {
    return FilterSelectChipUiHelper.usesFloatLabel({
      isFloatLabel: this.isFloatLabel,
      filtersUiVersion: this.filtersUiVersion,
      label: this.label
    });
  }

  /** При отключённом float подпись из `label` уходит в placeholder Prime. */
  public inputPlaceholder(): string {
    return FilterSelectChipUiHelper.inputPlaceholder({
      usesFloatLabel: this.usesFloatLabel(),
      label: this.label,
      placeholder: this.placeholder
    });
  }

  public get multiSelectDisplay(): string {
    if (this.isDisplayChip) {
      return FilterSelectChipUiHelper.chipOrCommaDisplay({
        display: 'chip',
        selectedCount: this._selectedItems?.length ?? 0,
        maxLabels: this.effectiveMaxSelectedLabels
      });
    }
    return this.display;
  }

  private isFilterChipMode(): boolean {
    return FilterSelectChipUiHelper.isFilterChipMode({
      filtersUiVersion: this.filtersUiVersion,
      usesFloatLabel: this.usesFloatLabel(),
      label: this.label
    });
  }

  public get effectiveMaxSelectedLabels(): number {
    return FilterSelectChipUiHelper.effectiveMaxSelectedLabels({
      isFilterChipMode: this.isFilterChipMode(),
      maxSelectedLabels: this.maxSelectedLabels
    });
  }

  private chipAutosize: FilterChipAutosizeHelper | null = null;
  private viewInitialized = false;
  private readonly cdRef = inject(ChangeDetectorRef);

  public constructor(
    private readonly renderer2: Renderer2,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public ngOnInit() {
    this.refreshChipDisplayMode();
  }

  public ngAfterViewInit() {
    this.viewInitialized = true;
    this.setupChipAutosize();
    this.refreshChipAutosize();
  }

  public ngAfterContentInit() {
    this.refreshChipDisplayMode();
  }

  public ngOnDestroy() {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.display?.currentValue === '') {
      this.showToggleAll = true;
    }
    if (changes.display) {
      this.refreshChipDisplayMode();
    }
    if (
      this.viewInitialized &&
      (changes.label || changes.filtersUiVersion || changes.isFloatLabel)
    ) {
      this.setupChipAutosize();
    }
    if (this.viewInitialized && (changes.selectedItems || changes.options || changes.selectedItemsLabel)) {
      this.refreshChipAutosize();
    }
  }

  /**
   * Prime chips: display="chip" или display="" без своего #selectedItemsTemplate (legacy: "" + toggle all).
   * Иначе — кастомный блок selectedItems (текст/запятая и т.д.).
   */
  private refreshChipDisplayMode(): void {
    const d = this.display;
    if (d === 'comma') {
      this.isDisplayChip = false;
      return;
    }
    if (d === 'chip') {
      this.isDisplayChip = true;
      return;
    }
    if (d === '') {
      this.isDisplayChip = !this.selectedItemsTemplate;
      return;
    }
    this.isDisplayChip = true;
  }

  public onModelChange(value: any[]) {
    if (this.selectionEmitSuppressDepth > 0) {
      return;
    }

    this._selectedItems = value ?? [];
    const values = this.isNotIdArray ? this._selectedItems : this.resolveSelectedItemsForEmit(this._selectedItems);
    this.lastSelectedItemsInput = values ?? [];

    if (this.timerFromChange) {
      clearTimeout(this.timerFromChange);
    }
    if (this.isNotIdArray) {
      this.changeValue.emit(this._selectedItems);
      this.selectedItemsChange.emit(this._selectedItems);
    } else {
      this.changeValue.emit(values);
      this.selectedItemsChange.emit(values);
    }
    if (this.isSendFromChange) {
      this.timerFromChange = setTimeout(() => {
        this.onFinalChange.emit(
          new FilterBySelectedItemsPipe().transform(this._selectedItems, this._options, this.optionValue, this.group)
        );
      }, this.TIMER_FULL_CHANGE);
    }
    if (this.multiSelect?.filterValue?.length) {
      this.multiSelect.resetFilter();
      this.onFilter.emit({ filter: '', originalEvent: new Event('filter') });
    }
    this.refreshChipAutosize();
  }

  public onPanelHide() {
    if (this.multiSelect?.filterValue?.length) {
      this.multiSelect.resetFilter();
      this.onFilter.emit({ filter: '', originalEvent: new Event('filter') });
    }

    setTimeout(() => {
      this.panelHide.emit(this.resolveSelectedItemsForEmit(this._selectedItems));
    }, 0);
    this.openedChange.emit(false);
    this.refreshChipAutosize();
  }

  public onFilterItems(event: any) {
    if (
      event &&
      document.querySelector('.p-overlay')?.getBoundingClientRect()?.height <
        window.outerHeight - document.querySelector('p-multiSelect').getBoundingClientRect().bottom &&
      this.moveDown
    ) {
      this.renderer2.setStyle(document.querySelector('.p-overlay'), 'top', '1030px', 1);
    }
    if (
      event &&
      document.querySelector('.p-overlay')?.getBoundingClientRect()?.height >
        window.outerHeight - document.querySelector('p-multiSelect').getBoundingClientRect().bottom &&
      this.moveDown
    ) {
      this.renderer2.setStyle(document.querySelector('.p-overlay'), 'top', '647px', 1);
    }
    this.onFilter.emit(event);
  }

  public onClear() {
    // PrimeNG removeOption() тоже шлёт onClear после снятия одного чипа.
    if (this._selectedItems?.length) {
      return;
    }

    this.lastSelectedItemsInput = [];
    this.runWithoutSelectionEmit(() => {
      this._selectedItems = [];
      this.sortedOptions();
      this.refreshChipAutosize();
    });
    this.changeValue.emit([]);
    this.selectedItemsChange.emit([]);
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.multiSelect?.filterValue);
  }

  public syncOptions(options: any[]): void {
    this.runWithoutSelectionEmit(() => this.buildOptions(options));
  }

  public setSelectionSilently(selectedItems: any[]): void {
    this.lastSelectedItemsInput = selectedItems ?? [];
    this.runWithoutSelectionEmit(() => this.buildSelectedItems(this.lastSelectedItemsInput));
  }

  private runWithoutSelectionEmit(action: () => void): void {
    this.selectionEmitSuppressDepth++;
    try {
      action();
      this.cdRef.markForCheck();
    } finally {
      // Use macrotask so PrimeNG ngModelChange after CD stays suppressed
      setTimeout(() => {
        this.selectionEmitSuppressDepth = Math.max(0, this.selectionEmitSuppressDepth - 1);
      });
    }
  }

  private resolveSelectedItemsForEmit(selectedKeys: any[]): any[] {
    if (!selectedKeys?.length) {
      return [];
    }

    if (this.isNotIdArray) {
      return selectedKeys ?? [];
    }

    const fromOptions = new FilterBySelectedItemsPipe().transform(
      selectedKeys,
      this._options,
      this.optionValue,
      this.group
    );
    const merged = [...fromOptions];

    if (typeof this.lastSelectedItemsInput?.[0] === 'object' && this.lastSelectedItemsInput[0]) {
      for (const item of this.lastSelectedItemsInput) {
        const key = item?.[this.optionValue];
        if (
          isNil(key) ||
          !selectedKeys.some((selectedKey) => isSameId(selectedKey, key)) ||
          merged.some((value) => isSameId(value?.[this.optionValue], key))
        ) {
          continue;
        }

        merged.push(item);
      }
    }

    return merged;
  }

  private buildOptions(options: any[]) {
    if (!Array.isArray(options)) {
      return;
    }

    if (typeof options[0] === 'string' || typeof options[0] === 'number') {
      this._options = options
        .filter((item, index) => options.indexOf(item) === index)
        .map((item) => ({
          name: item,
          value: item
        }));
      this.isNotIdArray = true;
      this.optionValue = 'value';
    } else {
      this._options = this.mergeSelectedIntoOptions(options ?? []);
    }

    this.sortedOptions();
    this.cdRef.markForCheck();
  }

  private mergeSelectedIntoOptions(options: any[]): any[] {
    const merged = [...options];

    if (this.group) {
      return merged;
    }

    const selectedItems = this.getSelectedItemsForOptionsMerge();
    for (const item of selectedItems) {
      if (typeof item !== 'object' || !item) {
        continue;
      }

      const key = item[this.optionValue];
      if (isNil(key) || merged.some((option) => isSameId(option?.[this.optionValue], key))) {
        continue;
      }

      merged.push(item);
    }

    return merged;
  }

  private setupChipAutosize(): void {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;

    if (!this.isFilterChipMode()) {
      return;
    }

    this.chipAutosize = new FilterChipAutosizeHelper(this.elementRef.nativeElement, {
      resolveMeasureText: () => this.getChipAutosizeMeasureText()
    });
    this.chipAutosize.connect();
  }

  private getChipAutosizeMeasureText(): string | null {
    return FilterSelectChipUiHelper.resolveChipAutosizeMeasureText({
      selectedCount: this._selectedItems?.length ?? 0,
      maxLabels: this.effectiveMaxSelectedLabels,
      selectedItemsLabel: this.selectedItemsLabel,
      isChipDisplay: this.isDisplayChip,
      singleItemLabel: this.getSelectedItemDisplayLabel(this._selectedItems?.[0])
    });
  }

  private getSelectedItemDisplayLabel(selectedValue: any): string | null {
    if (isNil(selectedValue)) {
      return null;
    }

    if (this.isNotIdArray) {
      const option = this._options.find((opt) => opt.value === selectedValue);
      return option?.name?.toString() ?? selectedValue?.toString() ?? null;
    }

    if (this.group) {
      for (const group of this._options) {
        const option = group.items?.find((opt: any) => opt[this.optionValue] === selectedValue);
        if (option) {
          return option[this.optionLabel]?.toString() ?? null;
        }
      }
      return null;
    }

    const option = this._options.find((opt) => opt[this.optionValue] === selectedValue);
    return option?.[this.optionLabel]?.toString() ?? null;
  }

  private refreshChipAutosize(): void {
    this.chipAutosize?.scheduleSync();
  }

  private buildSelectedItems(selectedItems: any[]) {
    if (!Array.isArray(selectedItems) || !selectedItems.length) {
      this._selectedItems = [];
      this.sortedOptions();
      this.refreshChipAutosize();
      return;
    }

    if ((typeof selectedItems[0] === 'string' || typeof selectedItems[0] === 'number') && this.isNotIdArray) {
      this._selectedItems = this._options
        .filter((opt) => selectedItems.some((value) => isSameId(value, opt.value)))
        .map((opt) => opt.value);
    }

    if ((typeof selectedItems[0] === 'string' || typeof selectedItems[0] === 'number') && !this.isNotIdArray) {
      if (this.group) {
        let _selectedItems: any[] = [];
        this._options.forEach((group) => {
          _selectedItems = _selectedItems.concat(
            group.items
              .filter((opt) => selectedItems.some((value) => isSameId(value, opt[this.optionValue])))
              .map((opt) => opt[this.optionValue])
          );
        });
        this._selectedItems = _selectedItems;
      } else {
        this._selectedItems = this._options
          .filter((opt) => selectedItems.some((value) => isSameId(value, opt[this.optionValue])))
          .map((opt) => opt[this.optionValue]);
      }
    }

    if (typeof selectedItems[0] === 'object' && selectedItems[0]) {
      const keysFromObjects = selectedItems
        .map((item) => item?.[this.optionValue])
        .filter((key) => !isNil(key));

      if (this.group) {
        let _selectedItems: any[] = [];
        this._options.forEach((group) => {
          _selectedItems = _selectedItems.concat(
            group.items
              .filter((opt) =>
                selectedItems.some((si) => isSameId(si?.[this.optionValue], opt[this.optionValue]))
              )
              .map((opt) => opt[this.optionValue])
          );
        });
        this._selectedItems = _selectedItems;
      } else {
        this._selectedItems = keysFromObjects.length
          ? keysFromObjects
          : this._options
              .filter((opt) =>
                selectedItems.some((si) => isSameId(si?.[this.optionValue], opt[this.optionValue]))
              )
              .map((opt) => opt[this.optionValue]);
      }
    }

    if (!this._options?.length) {
      this._selectedItems = selectedItems.map((si) => si?.[this.optionValue] || si);
    }

    this._selectedItems = this.mergeMissingSelectedItemKeys(selectedItems, this._selectedItems);

    this.sortedOptions();
    this.refreshChipAutosize();
  }

  private mergeMissingSelectedItemKeys(selectedItems: any[], resolvedKeys: any[]): any[] {
    if (!selectedItems?.length) {
      return resolvedKeys ?? [];
    }

    const merged = [...(resolvedKeys ?? [])];

    if (typeof selectedItems[0] === 'object' && selectedItems[0]) {
      for (const item of selectedItems) {
        const key = item?.[this.optionValue];
        if (isNil(key) || merged.some((value) => isSameId(value, key))) {
          continue;
        }

        merged.push(key);
      }
      return merged;
    }

    if (typeof selectedItems[0] === 'string' || typeof selectedItems[0] === 'number') {
      for (const key of selectedItems) {
        if (!merged.some((value) => isSameId(value, key))) {
          merged.push(key);
        }
      }
    }

    return merged;
  }

  private getSelectedItemsForOptionsMerge(): any[] {
    const selectedKeys = this._selectedItems ?? [];
    if (!selectedKeys.length) {
      return [];
    }

    if (this.isNotIdArray) {
      return selectedKeys.map((key) => ({ name: key, value: key }));
    }

    const fromInput = (this.lastSelectedItemsInput ?? []).filter((item) =>
      typeof item === 'object' &&
      item &&
      selectedKeys.some((key) => isSameId(key, item?.[this.optionValue]))
    );

    if (fromInput.length) {
      return fromInput;
    }

    return this._options.filter((option) =>
      selectedKeys.some((key) => isSameId(key, option?.[this.optionValue]))
    );
  }

  private hasSameSelectedItemsInput(next: any[]): boolean {
    if (next.length !== this.lastSelectedItemsInput.length) {
      return false;
    }

    if (!next.length) {
      return true;
    }

    if (typeof next[0] === 'object' && next[0]) {
      return next.every((item) =>
        this.lastSelectedItemsInput.some((current) =>
          isSameId(current?.[this.optionValue], item?.[this.optionValue])
        )
      );
    }

    return next.every((key) =>
      this.lastSelectedItemsInput.some((current) => isSameId(current, key))
    );
  }

  private sortedOptions() {
    if (this.isFilterSelectedItems && this._options?.length && this._selectedItems?.length) {
      const key = this.isNotIdArray ? 'value' : this.optionValue;
      this._options = this._options.map((option) => ({
        ...option,
        isSelected:
          this._selectedItems.findIndex((selectedItem) => isSameId(selectedItem, option?.[key])) !== -1
      }));
    }
  }
}
